import { useState, useEffect } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

const AdminView = ({ loaded }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/admin/users');
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDeleteClick = async (user) => {
    setDeleteTarget(user);
    setConfirmText('');
    setPreview(null);
    setPreviewLoading(true);
    try {
      const res = await apiFetch(`/api/admin/users/${user.id}/preview`);
      if (!res.ok) throw new Error('Failed to load preview');
      const data = await res.json();
      setPreview(data);
    } catch (err) {
      setPreview({ error: err.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || confirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/admin/users/${deleteTarget.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Delete failed');
      }
      setDeleteTarget(null);
      setPreview(null);
      setConfirmText('');
      setSuccessMessage(`Deleted user ${deleteTarget.full_name || deleteTarget.id}`);
      setTimeout(() => setSuccessMessage(null), 4000);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const closeModal = () => {
    setDeleteTarget(null);
    setPreview(null);
    setConfirmText('');
  };

  const countLabels = {
    memberships: 'Memberships',
    vendors: 'Vendors',
    invoices: 'Invoices',
    invoice_line_items: 'Invoice line items',
    integrations: 'Integrations',
    invites: 'Invites',
    ingest_emails: 'Email configs',
    vendor_folders: 'Vendor folders',
    vendor_integrations: 'Vendor integrations',
    vendor_folder_members: 'Folder members',
    s3_invoice_files: 'S3 invoice files',
    s3_ingest_files: 'S3 ingest emails',
  };

  return (
    <div className="max-w-[1000px] mx-auto">
      {/* Header */}
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '300ms',
        }}
      >
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl font-light text-zinc-900">Admin</h1>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">
            Superadmin
          </span>
        </div>
        <p className="text-sm text-zinc-400">
          Manage platform users. Deleting a user removes their Auth0 account, owned companies, and all associated data.
        </p>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="mb-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-700">
          {successMessage}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600 flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-4">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3l8 8M11 3l-8 8" /></svg>
          </button>
        </div>
      )}

      {/* Users table */}
      <div
        className="bg-white border border-zinc-100 rounded-lg overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '400ms',
        }}
      >
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h2 className="text-sm font-medium text-zinc-900">Users</h2>
          <span className="text-xs text-zinc-400">{users.length} total</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center text-sm text-zinc-400">No users found</div>
        ) : (
          <div className="divide-y divide-zinc-50">
            {users.map((u) => (
              <div key={u.id} className="px-5 py-4 flex items-center gap-4 hover:bg-zinc-50/50 transition-colors">
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-xs text-zinc-500 font-medium shrink-0">
                  {u.full_name ? u.full_name.charAt(0).toUpperCase() : '?'}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-900 truncate">{u.full_name || 'Unnamed'}</p>
                  <p className="text-xs text-zinc-400 truncate">{u.email || u.id}</p>
                </div>

                {/* Companies */}
                <div className="hidden sm:flex items-center gap-1.5 shrink-0">
                  {u.companies.length === 0 ? (
                    <span className="text-[10px] text-zinc-300">No companies</span>
                  ) : (
                    u.companies.map((c) => (
                      <span
                        key={c.companyId}
                        className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          c.role === 'owner'
                            ? 'bg-zinc-900 text-white'
                            : c.role === 'admin'
                            ? 'bg-zinc-200 text-zinc-700'
                            : 'bg-zinc-100 text-zinc-500'
                        }`}
                      >
                        {c.companyName} ({c.role})
                      </span>
                    ))
                  )}
                </div>

                {/* User ID */}
                <span className="hidden md:block text-[10px] font-mono text-zinc-300 shrink-0 w-24 truncate">
                  {u.id}
                </span>

                {/* Delete button */}
                <button
                  onClick={() => handleDeleteClick(u)}
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-medium text-red-500 hover:text-white hover:bg-red-500 border border-red-200 hover:border-red-500 rounded-md transition-colors shrink-0"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeModal}>
          <div
            className="bg-white rounded-lg max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="px-5 py-4 border-b border-zinc-100">
              <h3 className="text-sm font-medium text-red-600">Delete User</h3>
              <p className="text-xs text-zinc-400 mt-1">
                {deleteTarget.full_name || 'Unnamed'} ({deleteTarget.email || deleteTarget.id})
              </p>
            </div>

            {/* Modal body */}
            <div className="p-5 space-y-4">
              {previewLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
                </div>
              ) : preview?.error ? (
                <div className="text-sm text-red-500">{preview.error}</div>
              ) : preview ? (
                <>
                  {/* Owned companies */}
                  {preview.ownedCompanies && preview.ownedCompanies.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                      <p className="text-sm font-medium text-amber-800 mb-2">
                        Owned companies that will be deleted:
                      </p>
                      {preview.ownedCompanies.map((c) => (
                        <div key={c.id} className="flex items-center gap-2 text-sm text-amber-700">
                          <span className="font-medium">{c.name}</span>
                          <span className="text-[10px] font-mono text-amber-400">{c.id}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Cascade counts */}
                  {preview.counts && Object.keys(preview.counts).length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <p className="text-sm font-medium text-red-800 mb-2">
                        Records that will be deleted:
                      </p>
                      <div className="space-y-1">
                        {Object.entries(preview.counts).map(([table, count]) => (
                          <div key={table} className="flex items-center justify-between text-sm">
                            <span className="text-red-700">{countLabels[table] || table}</span>
                            <span className="text-red-500 font-mono text-xs">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {preview.ownedCompanies?.length === 0 && Object.keys(preview.counts || {}).length === 0 && (
                    <p className="text-sm text-zinc-500">
                      This user has no owned companies. Only the user account and their memberships will be removed.
                    </p>
                  )}

                  <p className="text-xs text-zinc-400">
                    Other members of these companies will keep their accounts but lose access to the deleted companies.
                  </p>

                  {/* Confirm input */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">
                      Type DELETE to confirm
                    </label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="DELETE"
                      className="w-full px-4 py-2.5 border border-zinc-200 text-sm rounded-md outline-none focus:border-red-400 transition-colors"
                      autoFocus
                    />
                  </div>
                </>
              ) : null}
            </div>

            {/* Modal footer */}
            <div className="px-5 py-4 border-t border-zinc-100 flex items-center justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={confirmText !== 'DELETE' || deleting || previewLoading}
                className="px-5 py-2 bg-red-600 text-white text-xs tracking-wider uppercase rounded-md hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {deleting && (
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                )}
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminView;
